import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Printer, ArrowLeft } from 'lucide-react';
import { useInvoice } from '@/hooks/useInvoices';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { formatFCFA, formatNumber } from '@/utils/currency';
import { INVOICE_STATUS_LABELS, INVOICE_TYPE_LABELS } from '@/types';
import { extractError } from '@/api/client';

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });

/**
 * Vue imprimable A4 d'une facture (proforma ou finale).
 * Route : /finance/invoices/:id/print
 * - Pas de sidebar/header app (layout autonome)
 * - Watermark "PROFORMA" si status proforma
 * - Boutons Imprimer + Retour masqués à l'impression
 *
 * NOTE : auto-print désactivé côté Chrome récent quand la page n'a pas
 * d'interaction utilisateur. On garde un délai pour laisser fonts/logos charger
 * puis on déclenche window.print(), avec fallback bouton "Imprimer maintenant"
 * toujours visible.
 */
export const InvoicePrintView = () => {
  const { id } = useParams<{ id: string }>();
  const { data: invoice, isLoading, error } = useInvoice(id);
  const filiale = useAuthStore((s) => s.filiale);
  const [autoPrintTriggered, setAutoPrintTriggered] = useState(false);

  // Auto-déclenche la boîte d'impression dès que la facture est chargée
  // Délai 800ms pour laisser les fonts + logos charger (sinon impression dégradée)
  useEffect(() => {
    if (invoice && !isLoading && !autoPrintTriggered) {
      const timer = setTimeout(() => {
        try {
          window.print();
        } catch (e) {
          // Certains navigateurs bloquent l'auto-print sans interaction user.
          // Le bouton manuel reste disponible.
          // eslint-disable-next-line no-console
          console.warn('[InvoicePrintView] auto-print bloqué, utilisez le bouton manuel', e);
        }
        setAutoPrintTriggered(true);
      }, 800);
      return () => clearTimeout(timer);
    }
  }, [invoice, isLoading, autoPrintTriggered]);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="text-sm text-gray-500">Chargement de la facture…</div>
        <Link to="/finance/invoices" className="mt-3 inline-block text-xs text-gray-400 hover:underline">
          ← Annuler
        </Link>
      </div>
    );
  }
  if (error || !invoice) {
    const errInfo = error ? extractError(error) : { code: 'NOT_FOUND', message: 'Facture introuvable' };
    return (
      <div className="p-8">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <p className="font-semibold">⚠ Impossible de charger la facture pour impression</p>
          <p className="mt-2 text-xs font-mono">Code : {errInfo.code}</p>
          <p className="text-xs">Message : {errInfo.message}</p>
          <p className="mt-2 text-xs text-red-600">
            ID facture : <span className="font-mono">{id ?? '(absent)'}</span>
          </p>
        </div>
        <Link to="/finance/invoices" className="mt-3 inline-block text-sm text-blue-600 hover:underline">
          ← Retour aux factures
        </Link>
      </div>
    );
  }

  const isProforma = invoice.status === 'proforma';
  const docTitle   = isProforma ? 'FACTURE PROFORMA' : 'FACTURE';
  const contact    = invoice.contact;

  // Calcul des totaux à partir des lignes (cohérent avec backend)
  const subtotalBrutCents = Number(BigInt(invoice.subtotalCents));   // HT brut (avant remise)
  const remisePct         = parseFloat(invoice.remisePct ?? '0');
  const remiseCents       = Math.round(subtotalBrutCents * remisePct / 100);
  const subtotalNetCents  = subtotalBrutCents - remiseCents;
  const taxCents          = Number(BigInt(invoice.taxCents));
  const totalCents        = Number(BigInt(invoice.totalCents));
  const tvaRate           = subtotalNetCents > 0 ? Math.round((taxCents / subtotalNetCents) * 100 * 10) / 10 : 0;

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Barre d'actions — masquée à l'impression */}
      <div className="sticky top-0 z-20 flex items-center justify-between bg-white border-b border-gray-200 px-6 py-3 shadow-sm print:hidden">
        <Link to="/finance/invoices">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="size-4" /> Retour aux factures
          </Button>
        </Link>
        <div className="flex items-center gap-3">
          {autoPrintTriggered && (
            <span className="hidden text-xs text-gray-500 sm:inline">
              Si la fenêtre d'impression ne s'est pas ouverte, cliquez ci-contre →
            </span>
          )}
          <Button
            size="sm"
            onClick={() => window.print()}
            style={{ backgroundColor: filiale?.colorPrimary }}
          >
            <Printer className="size-4" /> Imprimer / PDF
          </Button>
        </div>
      </div>

      {/* Document A4 */}
      <div className="mx-auto my-8 max-w-[210mm] bg-white p-12 shadow-lg print:my-0 print:max-w-full print:p-8 print:shadow-none relative">
        {isProforma && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center justify-center"
            style={{
              transform: 'rotate(-30deg)',
              opacity: 0.08,
              fontSize: '140px',
              fontWeight: 900,
              color: filiale?.colorPrimary ?? '#000',
              letterSpacing: '0.1em',
            }}
          >
            PROFORMA
          </div>
        )}

        {/* En-tête */}
        <header className="flex items-start justify-between border-b-2 pb-6" style={{ borderColor: filiale?.colorPrimary ?? '#000' }}>
          <div className="flex items-start gap-4">
            {filiale?.logoPath && (
              <img
                src={filiale.logoPath.startsWith('/') ? filiale.logoPath : `/${filiale.logoPath}`}
                alt={filiale.name}
                className="size-20 object-contain"
              />
            )}
            <div>
              <h2 className="text-xl font-bold" style={{ color: filiale?.colorPrimary }}>{filiale?.fullName ?? filiale?.name}</h2>
              <p className="text-xs text-gray-600">{filiale?.pays}</p>
              <p className="mt-2 text-[11px] text-gray-500">Devise : {filiale?.devise}</p>
            </div>
          </div>
          <div className="text-right">
            <h1 className="text-3xl font-bold tracking-wide" style={{ color: filiale?.colorPrimary }}>{docTitle}</h1>
            <p className="mt-1 text-sm font-mono text-gray-700">N° {invoice.reference}</p>
            <p className="text-xs text-gray-500">
              Statut : <strong>{INVOICE_STATUS_LABELS[invoice.status]}</strong>
              {invoice.version > 1 && <span className="ml-2 text-purple-700">· v{invoice.version}</span>}
            </p>
          </div>
        </header>

        {/* Bloc client + dates */}
        <section className="mt-6 grid grid-cols-2 gap-8">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">
              {invoice.invoiceType === 'client' ? 'Client' : 'Fournisseur'}
            </p>
            <p className="mt-1 text-sm font-semibold text-gray-900">
              {contact?.companyName ?? contact?.fullName ?? '—'}
            </p>
            {contact?.companyName && contact?.fullName && (
              <p className="text-xs text-gray-600">À l'attention de : {contact.fullName}</p>
            )}
            {contact?.email && <p className="text-xs text-gray-500">{contact.email}</p>}
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-600">
              <span className="text-gray-400">Type :</span> <strong>{INVOICE_TYPE_LABELS[invoice.invoiceType]}</strong>
            </p>
            <p className="text-xs text-gray-600">
              <span className="text-gray-400">Émise le :</span> <strong>{fmtDate(invoice.issueDate)}</strong>
            </p>
            <p className="text-xs text-gray-600">
              <span className="text-gray-400">Échéance :</span> <strong>{fmtDate(invoice.dueDate)}</strong>
            </p>
          </div>
        </section>

        {/* Tableau lignes */}
        <section className="mt-8">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b-2" style={{ borderColor: filiale?.colorPrimary ?? '#000' }}>
                <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-600">Réf</th>
                <th className="px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-gray-600">Désignation</th>
                <th className="px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">Qté</th>
                <th className="px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">PU HT</th>
                <th className="px-2 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-gray-600">Total HT</th>
              </tr>
            </thead>
            <tbody>
              {invoice.lines.map((line) => (
                <tr key={line.id} className="border-b border-gray-100">
                  <td className="px-2 py-2 font-mono text-[11px] text-gray-500">{line.produit?.code ?? '—'}</td>
                  <td className="px-2 py-2 text-gray-900">{line.designation}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{formatNumber(parseFloat(line.quantite))}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{formatFCFA(line.prixUnitaireCents)}</td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold text-gray-900">{formatFCFA(line.totalCents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Totaux */}
        <section className="mt-6 flex justify-end">
          <div className="w-72 space-y-1 text-sm">
            <div className="flex justify-between border-b border-gray-100 pb-1">
              <span className="text-gray-600">{remisePct > 0 ? 'Total HT brut' : 'Total HT'}</span>
              <span className="font-semibold tabular-nums text-gray-900">{formatFCFA(subtotalBrutCents)}</span>
            </div>
            {remisePct > 0 && (
              <>
                <div className="flex justify-between border-b border-gray-100 pb-1">
                  <span className="text-orange-700">Remise ({remisePct}%)</span>
                  <span className="tabular-nums text-orange-700">- {formatFCFA(remiseCents)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-1">
                  <span className="font-medium text-gray-700">Total HT net</span>
                  <span className="font-semibold tabular-nums text-gray-900">{formatFCFA(subtotalNetCents)}</span>
                </div>
              </>
            )}
            <div className="flex justify-between border-b border-gray-100 pb-1">
              <span className="text-gray-600">TVA ({tvaRate}%)</span>
              <span className="tabular-nums text-gray-700">{formatFCFA(taxCents)}</span>
            </div>
            <div
              className="mt-2 flex justify-between rounded-md px-3 py-2 text-white"
              style={{ backgroundColor: filiale?.colorPrimary ?? '#000' }}
            >
              <span className="text-xs uppercase tracking-wider">Total TTC</span>
              <span className="text-base font-bold tabular-nums">{formatFCFA(totalCents)}</span>
            </div>
          </div>
        </section>

        {/* ─── Conditions de paiement ─── */}
        {invoice.paymentTerms && (
          <section
            className="mt-8 rounded-md border-l-4 px-4 py-3"
            style={{
              borderColor: filiale?.colorPrimary ?? '#000',
              backgroundColor: `${filiale?.colorPrimary ?? '#000'}0D`, // ~5% opacity
            }}
          >
            <p
              className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: filiale?.colorPrimary ?? '#000' }}
            >
              Conditions de paiement
            </p>
            <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-gray-800">
              {invoice.paymentTerms}
            </p>
          </section>
        )}

        {/* Mentions */}
        <footer className="mt-12 border-t border-gray-200 pt-4 text-[10px] text-gray-500">
          {isProforma ? (
            <p>
              <strong>Ce document est une facture proforma</strong> — il ne constitue pas une facture comptable.
              Une facture finale sera émise après validation et acceptation par le client.
            </p>
          ) : (
            <p>
              Facture conforme OHADA — règlement à réception, sauf accord préalable. TVA acquittée sur les débits.
            </p>
          )}
          <p className="mt-2 text-center font-mono text-[9px] text-gray-400">
            {filiale?.fullName} · {filiale?.pays} · Document généré le {new Date().toLocaleDateString('fr-FR')}
          </p>
        </footer>
      </div>
    </div>
  );
};
